import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../../../api/supabase'
import WarehouseLayout from '../WarehouseLayout'
import type { Devanning } from '../types'
import { fmtDate, fmtDateTime } from '../types'
import { useAuth } from '../../../contexts/AuthContext'
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import { SortableContext, arrayMove, horizontalListSortingStrategy } from '@dnd-kit/sortable'
import { useColumnPrefs } from '../useColumnPrefs'
import { SortableColHeader, ColumnToggleButton, computeAutoFitWidths } from '../ColDragHeader'
import { useCustomersByRole } from '../../../hooks/useCustomersByRole'
import CustomerCombobox from '../../../components/CustomerCombobox'
import { useStickyHScroll } from '../../../hooks/useStickyHScroll'
import { StickyHScrollbar } from '../../../components/StickyHScrollbar'
import { usePagination } from '../../../hooks/usePagination'
import PaginationControls from '../../../components/PaginationControls'

const DEV_LIST_COL_DEFS = [
  { id: 'job_ref',      label: 'Job Ref' },
  { id: 'container',    label: 'Container' },
  { id: 'vessel',       label: 'Vessel' },
  { id: 'customer',     label: 'Customer' },
  { id: 'eta',          label: 'ETA' },
  { id: 'planned_devan', label: 'Planned Devan' },
  { id: 'started',      label: 'Started' },
  { id: 'completed',    label: 'Completed' },
  { id: 'status',       label: 'Status' },
  { id: 'ctns',         label: 'CTNS (Exp / Act)' },
] as const

const DEV_AUTO_WIDTHS = computeAutoFitWidths(DEV_LIST_COL_DEFS)

interface DevDeleteInfo {
  dev: DevanningRow
  outputCount: number
}

type SortCol = 'job_ref' | 'container_no' | 'planned_devan_at' | 'eta_date' | 'devanning_started_at' | 'status' | 'exp_ctns'

interface DevanningRow extends Devanning {
  exp_ctns: number
  act_ctns: number
  job_ref: string | null
  planned_devan_at: string | null
  plan_record_id: string | null
  last_reason: string | null
  last_changed_at: string | null
  last_changed_by: string | null
  customer_name: string | null
  source_loading_list_id: string | null
  pushed_to_inbound: boolean
  pushed_to_inbound_at: string | null
  pushed_to_inbound_by: string | null
}

const cellCls = 'w-full border border-blue-400 rounded px-1.5 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white dark:bg-gray-700 dark:text-white'

function InlineDatetime({ value, onCommit, onCancel }: { value: string; onCommit: (v: string) => void; onCancel: () => void }) {
  const [v, setV] = useState(value)
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => { ref.current?.focus() }, [])
  return <input ref={ref} type="datetime-local" className={cellCls} value={v} onChange={e => setV(e.target.value)}
    onBlur={() => onCommit(v)} onKeyDown={e => { if (e.key === 'Enter') onCommit(v); if (e.key === 'Escape') onCancel() }} />
}

function Spin() {
  return <svg className="animate-spin w-3 h-3 inline text-blue-500" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
}

function fmtPlannedDevan(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return `${d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} · ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
}

function toLocalDateTimeInput(iso: string): string {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function TotalCell({ exp, act }: { exp: number; act: number }) {
  if (exp === 0 && act === 0) return <span className="text-gray-400">—</span>
  const match = act === exp
  const diff = act - exp
  return (
    <span
      className={match ? 'text-green-700 dark:text-green-400' : 'text-amber-600 dark:text-amber-400 font-medium'}
      title={!match ? `Expected ${exp}, Actual ${act}, ${diff > 0 ? '+' : ''}${diff} mismatch` : undefined}
    >
      {exp} / {act}{!match && <span className="cursor-help"> ⚠</span>}
    </span>
  )
}

const DEV_STATUS_COLORS: Record<string, string> = {
  awaiting:  'bg-indigo-100 text-indigo-700',
  started:   'bg-yellow-100 text-yellow-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-600',
}

type ActionType = 'reschedule' | 'revert_to_pending' | 'revert_to_awaiting' | 'cancel'

export default function DevanningsPage() {
  const { id } = useParams<{ id: string }>()
  const { userEmail, user } = useAuth()
  const navigate = useNavigate()
  const [devannings, setDevannings] = useState<DevanningRow[]>([])
  const [loading, setLoading] = useState(true)
  const [sortCol, setSortCol] = useState<SortCol>('planned_devan_at')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const { customers } = useCustomersByRole('Cus')
  const [editCustId, setEditCustId] = useState<string | null>(null)
  const [savingCust, setSavingCust] = useState<string | null>(null)

  // Inline edit for planned_devan_at
  const [editPlanAt, setEditPlanAt] = useState<string | null>(null)
  const [savingPlanAt, setSavingPlanAt] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('active')
  const [devDeleteInfo, setDevDeleteInfo] = useState<DevDeleteInfo | null>(null)
  const [deleting, setDeleting] = useState(false)

  // 3-way cancel choice dialog
  const [cancelChoiceDev, setCancelChoiceDev] = useState<DevanningRow | null>(null)

  // Action dialog (reschedule / revert / cancel)
  const [actionDialog, setActionDialog] = useState<{ type: ActionType; dev: DevanningRow } | null>(null)
  const [actionReason, setActionReason] = useState('')
  const [rescheduleDate, setRescheduleDate] = useState('')
  const [actionSaving, setActionSaving] = useState(false)

  // Push to Inbound (from list, for already-completed devannings)
  const [pushDev, setPushDev] = useState<DevanningRow | null>(null)
  const [pushing, setPushing] = useState(false)

  // Column prefs
  const { order: colOrder, hidden: colHidden, visibleCols, setOrder: setColOrder, toggleHidden, reset: resetCols,
    extra: devExtra, setExtra: setDevExtra } =
    useColumnPrefs('col_pref_devannings', DEV_LIST_COL_DEFS, user?.id, { dbKey: 'col_order_devannings' }, { widths: {} })
  const devSavedWidths = (devExtra.widths as Record<string, number>) ?? {}
  const getDevColW = (id: string) => devSavedWidths[id] ?? DEV_AUTO_WIDTHS[id] ?? 120
  const saveDevColW = (id: string, w: number) => setDevExtra({ ...devExtra, widths: { ...devSavedWidths, [id]: w } })
  const resetDevColW = (id: string) => { const n = { ...devSavedWidths }; delete n[id]; setDevExtra({ ...devExtra, widths: n }) }
  const devSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))
  const handleDevColDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return
    const oi = colOrder.indexOf(active.id as string)
    const ni = colOrder.indexOf(over.id as string)
    if (oi !== -1 && ni !== -1) setColOrder(arrayMove(colOrder, oi, ni))
  }

  useEffect(() => {
    if (!id) return
    supabase.from('devannings')
      .select('*, plan:devanning_plans(id, container_no, vessel, eta_date, planned_devan_at, override_customer_id, override_customer:customers!override_customer_id(name), source_ll:loading_lists(id, job_ref, source_customer_id, source_customer:customers!source_customer_id(name))), items:devanning_items(expected_cartons, skip_pallet_detail, manual_carton_sum, pallets_detail)')
      .eq('warehouse_id', id)
      .then(({ data }) => {
        const rows = (data || []).map((d: any) => ({
          ...d,
          exp_ctns:              (d.items || []).reduce((s: number, it: any) => s + (it.expected_cartons || 0), 0),
          act_ctns:              (d.items || []).reduce((s: number, it: any) => s + (it.skip_pallet_detail ? (it.manual_carton_sum ?? 0) : (it.pallets_detail || []).reduce((a: number, v: number) => a + (v || 0), 0)), 0),
          job_ref:               d.plan?.source_ll?.job_ref ?? null,
          planned_devan_at:      d.plan?.planned_devan_at ?? null,
          plan_record_id:        d.plan?.id ?? null,
          last_reason:           d.last_reason ?? null,
          last_changed_at:       d.last_changed_at ?? null,
          last_changed_by:       d.last_changed_by ?? null,
          customer_name:         d.plan?.source_ll?.source_customer?.name ?? d.plan?.override_customer?.name ?? null,
          source_loading_list_id: d.plan?.source_ll?.id ?? null,
          pushed_to_inbound:     d.pushed_to_inbound ?? false,
          pushed_to_inbound_at:  d.pushed_to_inbound_at ?? null,
          pushed_to_inbound_by:  d.pushed_to_inbound_by ?? null,
        })) as DevanningRow[]
        setDevannings(rows)
        setLoading(false)
      })
  }, [id])

  const handleDeleteClick = async (dev: DevanningRow) => {
    const { count } = await supabase.from('devanning_outputs')
      .select('id', { count: 'exact', head: true }).eq('devanning_id', dev.id)
    setDevDeleteInfo({ dev, outputCount: count || 0 })
  }

  const handleDeleteConfirm = async () => {
    if (!devDeleteInfo) return
    const { dev } = devDeleteInfo
    setDeleting(true)
    try {
      await supabase.storage.from('warehouse-files')
        .remove([`devan-reports/${id}/${dev.id}.pdf`])
      await supabase.from('devanning_outputs').delete().eq('devanning_id', dev.id)
      await supabase.from('devanning_items').delete().eq('devanning_id', dev.id)
      await supabase.from('devannings').delete().eq('id', dev.id)
      setDevannings(prev => prev.filter(x => x.id !== dev.id))
      setDevDeleteInfo(null)
    } finally {
      setDeleting(false)
    }
  }

  const saveCustomer = useCallback(async (dev: DevanningRow, customerId: string) => {
    // #274: prefer updating the linked loading_list's customer (existing behaviour);
    // fall back to devanning_plans.override_customer_id when there's no source LL
    // (e.g. the LL was detached, #271) so the customer can still be (re)selected.
    if (!dev.source_loading_list_id && !dev.plan_record_id) return
    setSavingCust(dev.id)
    setEditCustId(null)
    try {
      const { error } = dev.source_loading_list_id
        ? await supabase.from('loading_lists').update({ source_customer_id: customerId || null }).eq('id', dev.source_loading_list_id)
        : await supabase.from('devanning_plans').update({ override_customer_id: customerId || null }).eq('id', dev.plan_record_id)
      if (error) throw error
      const cust = customers.find(c => c.id === customerId)
      setDevannings(prev => prev.map(d => d.id === dev.id ? { ...d, customer_name: cust?.name ?? null } : d))
    } catch (err: any) {
      alert(err.message)
    } finally {
      setSavingCust(null)
    }
  }, [customers])

  const handleSort = (col: SortCol) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortCol(col); setSortDir('asc') }
  }

  const savePlannedDevanAt = useCallback(async (dev: DevanningRow, localDtStr: string) => {
    if (!dev.plan_record_id) return
    setSavingPlanAt(dev.id)
    setEditPlanAt(null)
    try {
      const iso = localDtStr ? new Date(localDtStr).toISOString() : null
      const { error } = await supabase.from('devanning_plans')
        .update({ planned_devan_at: iso }).eq('id', dev.plan_record_id)
      if (error) throw error
      setDevannings(prev => prev.map(d => d.id === dev.id ? { ...d, planned_devan_at: iso } : d))
    } catch (err: any) {
      alert(err.message)
    } finally {
      setSavingPlanAt(null)
    }
  }, [])

  const openAction = (type: ActionType, dev: DevanningRow) => {
    setActionDialog({ type, dev })
    setActionReason('')
    setRescheduleDate(dev.planned_devan_at ? toLocalDateTimeInput(dev.planned_devan_at) : '')
  }

  const handleAction = async () => {
    if (!actionDialog || actionReason.trim().length < 5) return
    const { type, dev } = actionDialog
    setActionSaving(true)
    try {
      const now = new Date().toISOString()
      const actor = userEmail || 'unknown'
      const patch = { last_reason: actionReason, last_changed_at: now, last_changed_by: actor }

      if (type === 'reschedule') {
        const iso = rescheduleDate ? new Date(rescheduleDate).toISOString() : null
        if (dev.plan_record_id) {
          const { error } = await supabase.from('devanning_plans').update({ planned_devan_at: iso }).eq('id', dev.plan_record_id)
          if (error) throw error
        }
        await supabase.from('devannings').update(patch).eq('id', dev.id)
        setDevannings(prev => prev.map(d => d.id === dev.id ? { ...d, planned_devan_at: iso, ...patch } : d))
      } else if (type === 'revert_to_pending') {
        if (dev.plan_record_id) {
          const { error } = await supabase.from('devanning_plans').update({
            status: 'pending', planned_devan_at: null,
            last_reason: actionReason, last_changed_at: now, last_changed_by: actor,
          }).eq('id', dev.plan_record_id)
          if (error) throw error
        }
        await supabase.from('devanning_items').delete().eq('devanning_id', dev.id)
        const { error } = await supabase.from('devannings').delete().eq('id', dev.id)
        if (error) throw error
        setDevannings(prev => prev.filter(d => d.id !== dev.id))
        if (dev.plan_record_id) {
          navigate(`/warehouse/${id}/plans/${dev.plan_record_id}`)
        }
      } else if (type === 'revert_to_awaiting') {
        const { error } = await supabase.from('devannings').update({
          status: 'awaiting', devanning_started_at: null, ...patch,
        }).eq('id', dev.id)
        if (error) throw error
        setDevannings(prev => prev.map(d => d.id === dev.id ? { ...d, status: 'awaiting', devanning_started_at: null, ...patch } : d))
      } else { // Cancel Job — hard cancel, also cancels the plan
        const { error } = await supabase.from('devannings').update({ status: 'cancelled', ...patch }).eq('id', dev.id)
        if (error) throw error
        if (dev.plan_record_id) {
          await supabase.from('devanning_plans').update({
            status: 'cancelled', last_reason: actionReason, last_changed_at: now, last_changed_by: actor,
          }).eq('id', dev.plan_record_id)
        }
        setDevannings(prev => prev.map(d => d.id === dev.id ? { ...d, status: 'cancelled', ...patch } : d))
      }
      setActionDialog(null)
      setActionReason('')
      setRescheduleDate('')
    } catch (err: any) {
      alert(err.message)
    } finally {
      setActionSaving(false)
    }
  }

  const handlePushToInbound = async (dev: DevanningRow) => {
    setPushing(true)
    try {
      const { data: rawItems, error: itemsErr } = await supabase
        .from('devanning_items')
        .select('id, split_no, group_name, delivery_address, delivery_method, expected_cbm, expected_weight_kg, hbl_no, system_ref, description, customer_id, notes, skip_pallet_detail, manual_carton_sum, manual_pallet_total, pallets_detail, plan_item:devanning_plan_items(client_ref, delivery_contact)')
        .eq('devanning_id', dev.id)
        .order('id', { ascending: true })
      if (itemsErr) throw new Error(`Fetch items: ${itemsErr.message}`)

      const now = new Date().toISOString()
      // Group by group_name → ONE warehouse_inventory row per split (not per item)
      const groupMap = new Map<string, any[]>()
      for (const di of (rawItems || [])) {
        const key = di.group_name || '(No Group)'
        if (!groupMap.has(key)) groupMap.set(key, [])
        groupMap.get(key)!.push(di)
      }
      const inventoryRows = Array.from(groupMap.entries()).map(([groupName, items]) => {
        const first = items[0]
        let totalCtns = 0
        let totalPlts = 0
        for (const di of items) {
          const isSkip = di.skip_pallet_detail ?? true
          const rawPallets = di.pallets_detail
          const pVals: number[] = Array.isArray(rawPallets)
            ? rawPallets.map(Number)
            : Object.values(rawPallets ?? {}).map(Number)
          if (isSkip) {
            totalCtns += di.manual_carton_sum ?? 0
            totalPlts += di.manual_pallet_total ?? 0
          } else {
            totalCtns += pVals.reduce((s: number, v: number) => s + (v || 0), 0)
            totalPlts += pVals.filter((v: number) => v > 0).length
          }
        }
        const totalCbm = items.reduce((s: number, di: any) => s + (di.expected_cbm || 0), 0)
        const totalKgs = items.reduce((s: number, di: any) => s + (di.expected_weight_kg || 0), 0)
        const hbls = items.map((di: any) => di.hbl_no).filter(Boolean).join(', ')
        const sysRefs = [...new Set(items.map((di: any) => di.system_ref).filter(Boolean))].join(', ')
        const houseJobRefs = [...new Set(items.map((di: any) => (di.plan_item as any)?.client_ref).filter(Boolean))].sort().join(', ')
        return {
          warehouse_id:      dev.warehouse_id,
          devanning_id:      dev.id,
          devanning_item_id: first.id,
          job_ref:           dev.job_ref,
          split_no:          first.split_no || null,
          house_job_refs:    houseJobRefs || null,
          group_name:        groupName,
          destination:       first.delivery_address,
          hbl_no:            hbls || null,
          system_ref:        sysRefs || null,
          description:       items.map((di: any) => di.description).filter(Boolean).join('; ') || null,
          delivery_address:  first.delivery_address,
          delivery_method:   first.delivery_method,
          delivery_contact:  (first.plan_item as any)?.delivery_contact || null,
          customer_id:       first.customer_id || null,
          cartons:           totalCtns,
          pallets_qty:       totalPlts || null,
          cbm:               totalCbm || null,
          kgs:               totalKgs || null,
          notes:             items.map((di: any) => di.notes).filter(Boolean).join('; ') || null,
          status:            'in_inbound',
          inbound_at:        dev.devanning_completed_at || now,
        }
      })

      // Skip groups already in inventory (dedup by devanning_item_id)
      const itemIds = inventoryRows.map(r => r.devanning_item_id).filter(Boolean)
      const existingIds = new Set<string>()
      if (itemIds.length > 0) {
        const { data: existing, error: existErr } = await supabase
          .from('warehouse_inventory').select('devanning_item_id').in('devanning_item_id', itemIds)
        if (existErr) throw new Error(`Check existing: ${existErr.message}`)
        ;(existing || []).forEach((e: any) => { if (e.devanning_item_id) existingIds.add(e.devanning_item_id) })
      }
      const toInsert = inventoryRows.filter(r => !existingIds.has(r.devanning_item_id))

      if (toInsert.length > 0) {
        const { error: insErr } = await supabase.from('warehouse_inventory').insert(toInsert)
        if (insErr) throw new Error(`Insert inventory: ${insErr.message}`)
      }

      const { error: updErr } = await supabase.from('devannings').update({
        pushed_to_inbound: true,
        pushed_to_inbound_at: now,
        pushed_to_inbound_by: userEmail || 'unknown',
      }).eq('id', dev.id)
      if (updErr) throw new Error(`Update devanning: ${updErr.message}`)

      setDevannings(prev => prev.map(d => d.id === dev.id ? { ...d, pushed_to_inbound: true, pushed_to_inbound_at: now, pushed_to_inbound_by: userEmail || 'unknown' } : d))
      setPushDev(null)
    } catch (e: any) {
      console.error('Push to Inbound failed:', e)
      alert(`Push failed: ${e.message}`)
    } finally {
      setPushing(false)
    }
  }

  const visible = useMemo(() => {
    let result = filter === 'all' ? devannings : filter === 'active' ? devannings.filter(d => d.status === 'awaiting' || d.status === 'started') : devannings.filter(d => d.status === filter)
    if (search.trim()) {
      const q = search.toLowerCase()
      result = result.filter(d =>
        (d.job_ref || '').toLowerCase().includes(q) ||
        (d.container_no || '').toLowerCase().includes(q) ||
        (d.plan?.vessel || '').toLowerCase().includes(q) ||
        (d.customer_name || '').toLowerCase().includes(q)
      )
    }
    return [...result].sort((a, b) => {
      let va: any, vb: any
      if (sortCol === 'job_ref')                { va = a.job_ref; vb = b.job_ref }
      else if (sortCol === 'container_no')      { va = a.container_no; vb = b.container_no }
      else if (sortCol === 'planned_devan_at')  { va = a.planned_devan_at; vb = b.planned_devan_at }
      else if (sortCol === 'eta_date')          { va = a.plan?.eta_date; vb = b.plan?.eta_date }
      else if (sortCol === 'status')            { va = a.status; vb = b.status }
      else if (sortCol === 'exp_ctns')          { va = a.exp_ctns; vb = b.exp_ctns }
      else /* devanning_started_at */           { va = a.devanning_started_at; vb = b.devanning_started_at }
      if (va == null) return sortDir === 'asc' ? 1 : -1
      if (vb == null) return sortDir === 'asc' ? -1 : 1
      if (typeof va === 'number') return sortDir === 'asc' ? va - vb : vb - va
      return sortDir === 'asc' ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va))
    })
  }, [devannings, filter, search, sortCol, sortDir])

  const counts = useMemo(() => ({
    active:    devannings.filter(d => d.status === 'awaiting' || d.status === 'started').length,
    all:       devannings.length,
    awaiting:  devannings.filter(d => d.status === 'awaiting').length,
    started:   devannings.filter(d => d.status === 'started').length,
    completed: devannings.filter(d => d.status === 'completed').length,
    cancelled: devannings.filter(d => d.status === 'cancelled').length,
  }), [devannings])

  const { scrollProps: devScrollProps, contentRef: devTableRef, bar: devScrollBar } = useStickyHScroll([visible, visibleCols])

  const { page, setPage, pageSize, setPageSize } = usePagination('devannings-list')
  useEffect(() => { setPage(0) }, [search, filter, sortCol, sortDir]) // eslint-disable-line react-hooks/exhaustive-deps
  const pagedTotal = visible.length
  const paged = pageSize === 'all' ? visible : visible.slice(page * pageSize, page * pageSize + pageSize)

  const thBase = 'px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap'
  const thSort = `${thBase} cursor-pointer hover:text-gray-700 select-none`
  const dir = (col: SortCol) => sortCol === col ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''
  const statusLabel = (s: string) => s === 'awaiting' ? 'Awaiting' : s === 'started' ? 'Started' : s.charAt(0).toUpperCase() + s.slice(1)

  const handleStartDevanning = async (dev: DevanningRow) => {
    if (!window.confirm(`Start devanning for ${dev.container_no}? This will lock the plan for editing.`)) return
    const now = new Date().toISOString()
    const { error } = await supabase.from('devannings').update({ status: 'started', devanning_started_at: now, devanned_by: userEmail || 'unknown' }).eq('id', dev.id)
    if (error) { alert(error.message); return }
    setDevannings(prev => prev.map(d => d.id === dev.id ? { ...d, status: 'started', devanning_started_at: now, devanned_by: userEmail || 'unknown' } : d))
  }

  return (
    <WarehouseLayout warehouseId={id!}>
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="flex items-center gap-3 flex-wrap px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="relative">
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search job ref, container, vessel..."
              className="pl-8 pr-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 w-56" />
            <svg className="absolute left-2.5 top-2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <div className="flex gap-1 flex-wrap">
            {(['active', 'all', 'awaiting', 'started', 'completed', 'cancelled'] as const).map(s => (
              <button key={s} onClick={() => setFilter(s)}
                className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                  filter === s ? 'bg-blue-600 text-white' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}>
                {s === 'active' ? 'Active' : s === 'all' ? 'All' : statusLabel(s)} ({counts[s]})
              </button>
            ))}
          </div>
          <ColumnToggleButton cols={DEV_LIST_COL_DEFS} hidden={colHidden} onToggle={toggleHidden} onReset={resetCols}
            onRecalculate={() => { const w = computeAutoFitWidths(DEV_LIST_COL_DEFS); setDevExtra({ ...devExtra, widths: { ...w, ...devSavedWidths } }) }} />
        </div>
        {loading ? (
          <div className="py-16 text-center text-gray-400">Loading...</div>
        ) : (
          <div className="overflow-x-auto" {...devScrollProps}>
            <table ref={devTableRef as React.RefObject<HTMLTableElement>} className="min-w-full text-sm divide-y divide-gray-200 dark:divide-gray-700">
              <DndContext sensors={devSensors} collisionDetection={closestCenter} onDragEnd={handleDevColDragEnd}>
                <SortableContext items={visibleCols.map(c => c.id)} strategy={horizontalListSortingStrategy}>
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  {visibleCols.map(col => {
                    const sortKey = col.id === 'job_ref' ? 'job_ref' : col.id === 'container' ? 'container_no' : col.id === 'eta' ? 'eta_date' : col.id === 'planned_devan' ? 'planned_devan_at' : col.id === 'started' ? 'devanning_started_at' : col.id === 'status' ? 'status' : col.id === 'ctns' ? 'exp_ctns' : null
                    return sortKey
                      ? <SortableColHeader key={col.id} id={col.id} style={{ width: getDevColW(col.id), minWidth: '50px' }} className={thSort} onClick={() => handleSort(sortKey as SortCol)} onResizeEnd={w => saveDevColW(col.id, w)} onResizeReset={() => resetDevColW(col.id)}>{col.label}{dir(sortKey as SortCol)}</SortableColHeader>
                      : <SortableColHeader key={col.id} id={col.id} style={{ width: getDevColW(col.id), minWidth: '50px' }} className={thBase} onResizeEnd={w => saveDevColW(col.id, w)} onResizeReset={() => resetDevColW(col.id)}>{col.label}</SortableColHeader>
                  })}
                  <th className={thBase} />
                </tr>
              </thead>
                </SortableContext>
              </DndContext>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {paged.map(d => {
                  const custCell = (
                    <td key="customer" className="group px-4 py-3 text-xs cursor-pointer whitespace-nowrap"
                      onClick={() => !savingCust && (d.source_loading_list_id || d.plan_record_id) && setEditCustId(editCustId === d.id ? null : d.id)}>
                      {savingCust === d.id ? <Spin /> : editCustId === d.id ? (
                        <CustomerCombobox
                          value=""
                          onChange={v => { saveCustomer(d, v); setEditCustId(null) }}
                          onClose={() => setEditCustId(null)}
                          className="w-44"
                        />
                      ) : (
                        <span className="flex items-center gap-1 text-gray-600 dark:text-gray-300">
                          {d.customer_name || <span className="text-gray-300">—</span>}
                          {(d.source_loading_list_id || d.plan_record_id) && <svg className="w-3 h-3 text-gray-300 group-hover:text-gray-400 opacity-0 group-hover:opacity-100" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M9 13l6.5-6.5a2 2 0 012.828 2.828L11.828 15.828a2 2 0 01-1.414.586H8v-2.414a2 2 0 01.586-1.414z" /></svg>}
                        </span>
                      )}
                    </td>
                  )
                  const cells: Record<string, React.ReactNode> = {
                    job_ref: <td key="job_ref" className="px-4 py-3 font-mono text-xs text-gray-700 dark:text-gray-300 whitespace-nowrap">{d.job_ref || '—'}</td>,
                    container: <td key="container" className="px-4 py-3 font-mono text-xs text-gray-700 dark:text-gray-300 whitespace-nowrap">{d.container_no}</td>,
                    vessel: <td key="vessel" className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{d.plan?.vessel || '—'}</td>,
                    customer: custCell,
                    eta: <td key="eta" className="px-4 py-3 text-gray-500">{fmtDate(d.plan?.eta_date)}</td>,
                    planned_devan: (
                      <td key="planned_devan" className="group px-4 py-3 text-xs text-gray-600 dark:text-gray-300 cursor-text whitespace-nowrap"
                        onClick={() => editPlanAt !== d.id && savingPlanAt !== d.id && d.plan_record_id && setEditPlanAt(d.id)}>
                        {savingPlanAt === d.id ? <Spin /> : editPlanAt === d.id ? (
                          <InlineDatetime
                            value={d.planned_devan_at ? toLocalDateTimeInput(d.planned_devan_at) : ''}
                            onCommit={v => savePlannedDevanAt(d, v)}
                            onCancel={() => setEditPlanAt(null)} />
                        ) : (
                          <span className="flex items-center gap-1">
                            {fmtPlannedDevan(d.planned_devan_at)}
                            {d.plan_record_id && <svg className="w-3 h-3 text-gray-300 group-hover:text-gray-400 opacity-0 group-hover:opacity-100" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M9 13l6.5-6.5a2 2 0 012.828 2.828L11.828 15.828a2 2 0 01-1.414.586H8v-2.414a2 2 0 01.586-1.414z" /></svg>}
                          </span>
                        )}
                      </td>
                    ),
                    started: <td key="started" className="px-4 py-3 text-gray-500 text-xs">{fmtDateTime(d.devanning_started_at)}</td>,
                    completed: <td key="completed" className="px-4 py-3 text-gray-500 text-xs">{fmtDateTime(d.devanning_completed_at)}</td>,
                    status: (
                      <td key="status" className="px-4 py-3 whitespace-nowrap">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${DEV_STATUS_COLORS[d.status] || 'bg-gray-100 text-gray-600'}`}>
                          {statusLabel(d.status)}
                        </span>
                        {d.status === 'completed' && !d.pushed_to_inbound && (
                          <span className="ml-1.5 text-xs text-amber-600 font-medium">⚠ Not in Inbound</span>
                        )}
                        {d.status === 'completed' && d.pushed_to_inbound && (
                          <span className="ml-1.5 text-xs text-green-600 font-medium">✓ Pushed</span>
                        )}
                        {d.last_reason && (
                          <span
                            title={`Last change: ${d.last_reason}${d.last_changed_by ? ` — by ${d.last_changed_by}` : ''}${d.last_changed_at ? ` · ${fmtDateTime(d.last_changed_at)}` : ''}`}
                            className="ml-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-help inline-flex items-center align-middle">
                            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                          </span>
                        )}
                      </td>
                    ),
                    ctns: <td key="ctns" className="px-4 py-3 text-sm font-mono"><TotalCell exp={d.exp_ctns} act={d.act_ctns} /></td>,
                  }
                  const needsPush = d.status === 'completed' && !d.pushed_to_inbound
                  return (
                    <tr key={d.id} className={`hover:bg-gray-50 dark:hover:bg-gray-750 ${needsPush ? 'bg-yellow-50 dark:bg-yellow-900/10' : ''}`}>
                      {visibleCols.map(col => cells[col.id])}
                      <td className="px-4 py-3 space-x-2 text-xs whitespace-nowrap">
                        <Link to={`/warehouse/${id}/devannings/${d.id}`} className="text-blue-600 hover:underline font-medium">
                          Open Workflow
                        </Link>
                        {d.plan_record_id && (
                          <Link to={`/warehouse/${id}/plans/${d.plan_record_id}`} className="text-indigo-600 hover:underline">View Plan</Link>
                        )}
                        {(d.status === 'started' || d.status === 'completed') && (
                          <Link to={`/warehouse/${id}/devannings/${d.id}/report`} className="text-gray-500 hover:underline">Report</Link>
                        )}
                        <Link to={`/warehouse/${id}/devannings/${d.id}/notes`} className="text-gray-500 hover:underline">Notes</Link>
                        {(d.status === 'awaiting' || d.status === 'started') && (
                          <button onClick={() => openAction('reschedule', d)} className="text-blue-600 hover:underline">Reschedule</button>
                        )}
                        {d.status === 'awaiting' && (
                          <button onClick={() => handleStartDevanning(d)} className="text-green-600 hover:underline font-semibold">Start Devanning</button>
                        )}
                        {d.status === 'awaiting' && (
                          <button onClick={() => openAction('revert_to_pending', d)} className="text-amber-600 hover:underline">Revert to Pending</button>
                        )}
                        {d.status === 'started' && (
                          <button onClick={() => openAction('revert_to_awaiting', d)} className="text-amber-600 hover:underline">Revert to Awaiting</button>
                        )}
                        {(d.status === 'awaiting' || d.status === 'started') && (
                          <button onClick={() => setCancelChoiceDev(d)} className="text-red-500 hover:underline">Cancel</button>
                        )}
                        {d.status === 'completed' && !d.pushed_to_inbound && (
                          <button onClick={() => setPushDev(d)} className="text-orange-600 hover:underline font-semibold">Push to Inbound →</button>
                        )}
                        {d.status === 'completed' && d.pushed_to_inbound && (
                          <>
                            <Link to={`/warehouse/${id}/inbound`} className="text-blue-600 hover:underline">View in Inbound →</Link>
                            <button onClick={() => setPushDev(d)} className="text-gray-400 hover:text-orange-500 hover:underline text-[11px]">Re-push</button>
                          </>
                        )}
                        {(d.status === 'cancelled' || d.status === 'completed') && (
                          <button onClick={() => handleDeleteClick(d)} className="text-red-400 hover:text-red-600 hover:underline">Delete</button>
                        )}
                      </td>
                    </tr>
                  )
                })}
                {visible.length === 0 && (
                  <tr><td colSpan={visibleCols.length + 1} className="px-4 py-12 text-center text-gray-400">
                    {search || filter !== 'all' ? 'No results.' : 'No devannings yet.'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        {!loading && visible.length > 0 && (
          <PaginationControls page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} total={pagedTotal} />
        )}
      </div>
      <StickyHScrollbar {...devScrollBar} />
      {cancelChoiceDev && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-1">What do you want to do?</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-5 font-mono">{cancelChoiceDev.container_no}</p>
            <div className="space-y-2">
              <button
                onClick={() => { const d = cancelChoiceDev; setCancelChoiceDev(null); openAction('reschedule', d) }}
                className="w-full text-left px-4 py-3 rounded-xl border border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors">
                <div className="font-semibold text-blue-800 dark:text-blue-200 text-sm">📅 Reschedule</div>
                <div className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">Devanning delayed, still happens</div>
              </button>
              <button
                onClick={() => {
                  const d = cancelChoiceDev; setCancelChoiceDev(null)
                  openAction(d.status === 'started' ? 'revert_to_awaiting' : 'revert_to_pending', d)
                }}
                className="w-full text-left px-4 py-3 rounded-xl border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors">
                <div className="font-semibold text-amber-800 dark:text-amber-200 text-sm">
                  ↩️ {cancelChoiceDev.status === 'started' ? 'Revert to Awaiting' : 'Revert to Pending'}
                </div>
                <div className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                  {cancelChoiceDev.status === 'started' ? 'Pause, reopen via Awaiting' : 'Soft-cancel, reshuffle via Plan'}
                </div>
              </button>
              <button
                onClick={() => { const d = cancelChoiceDev; setCancelChoiceDev(null); openAction('cancel', d) }}
                className="w-full text-left px-4 py-3 rounded-xl border border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors">
                <div className="font-semibold text-red-800 dark:text-red-200 text-sm">❌ Cancel Job</div>
                <div className="text-xs text-red-600 dark:text-red-400 mt-0.5">Hard-cancel: devanning + plan permanently cancelled</div>
              </button>
            </div>
            <button onClick={() => setCancelChoiceDev(null)}
              className="mt-4 w-full px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300">
              Back
            </button>
          </div>
        </div>
      )}
      {actionDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-0.5">
              {actionDialog.type === 'reschedule' ? 'Reschedule Devanning' : actionDialog.type === 'revert_to_pending' ? 'Revert to Pending' : actionDialog.type === 'revert_to_awaiting' ? 'Revert to Awaiting' : 'Cancel Devanning'}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 font-mono">{actionDialog.dev.container_no}</p>

            {actionDialog.type === 'reschedule' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">New Date & Time <span className="text-red-500">*</span></label>
                <input type="datetime-local" value={rescheduleDate} onChange={e => setRescheduleDate(e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            )}

            {actionDialog.type === 'revert_to_pending' && (
              <p className="text-sm text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg px-3 py-2 mb-4">
                This will delete this devanning record and return the plan to Pending status.
              </p>
            )}
            {actionDialog.type === 'revert_to_awaiting' && (
              <p className="text-sm text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg px-3 py-2 mb-4">
                This will return the devanning to Awaiting status. Pallet data will be preserved. The plan will become editable again.
              </p>
            )}
            {actionDialog.type === 'cancel' && (
              <p className="text-sm text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg px-3 py-2 mb-4">
                ⚠️ This will permanently cancel both the devanning and its plan. This cannot be undone.
              </p>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Reason <span className="text-red-500">*</span> <span className="text-gray-400 font-normal text-xs">(min 5 chars)</span>
              </label>
              <textarea rows={3} value={actionReason} onChange={e => setActionReason(e.target.value)}
                placeholder="Enter reason for this change..."
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
              {actionReason.length > 0 && actionReason.trim().length < 5 && (
                <p className="mt-1 text-xs text-red-500">Reason must be at least 5 characters.</p>
              )}
            </div>

            <div className="flex justify-end gap-3">
              <button onClick={() => { setActionDialog(null); setActionReason('') }}
                className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300">
                Cancel
              </button>
              <button
                disabled={actionSaving || actionReason.trim().length < 5 || (actionDialog.type === 'reschedule' && !rescheduleDate)}
                onClick={handleAction}
                className={`px-4 py-2 text-sm rounded-lg text-white font-medium disabled:opacity-50 ${
                  actionDialog.type === 'reschedule' ? 'bg-blue-600 hover:bg-blue-700' :
                  (actionDialog.type === 'revert_to_pending' || actionDialog.type === 'revert_to_awaiting') ? 'bg-amber-600 hover:bg-amber-700' :
                  'bg-red-600 hover:bg-red-700'
                }`}>
                {actionSaving ? 'Saving…' :
                  actionDialog.type === 'reschedule' ? 'Confirm Reschedule' :
                  actionDialog.type === 'revert_to_pending' ? 'Revert to Pending' :
                  actionDialog.type === 'revert_to_awaiting' ? 'Revert to Awaiting' :
                  'Cancel Devanning'}
              </button>
            </div>
          </div>
        </div>
      )}
      {devDeleteInfo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Confirm Delete</h3>
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">
              Delete Devanning &ldquo;<strong>{devDeleteInfo.dev.container_no}</strong>&rdquo;?
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
              This will also delete: devanning items
              {devDeleteInfo.outputCount > 0 && `, ${devDeleteInfo.outputCount} PDF report(s)`}.
            </p>
            <p className="text-sm font-semibold text-red-600 mb-4">This cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDevDeleteInfo(null)} disabled={deleting}
                className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
                Cancel
              </button>
              <button onClick={handleDeleteConfirm} disabled={deleting}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
                {deleting ? 'Deleting…' : 'Confirm Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Push to Inbound confirm dialog */}
      {pushDev && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-2xl">📦</span>
              <h3 className="font-semibold text-gray-900 dark:text-white text-lg">Push to Inbound</h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">
              <span className="font-mono font-semibold">{pushDev.job_ref || '—'}</span>
              {pushDev.container_no && <span className="ml-2 text-gray-400">· {pushDev.container_no}</span>}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
              This will create inventory rows from the devanning splits and mark them as In Inbound.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setPushDev(null)} disabled={pushing}
                className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300">
                Cancel
              </button>
              <button onClick={() => handlePushToInbound(pushDev)} disabled={pushing}
                className="px-4 py-2 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 font-medium">
                {pushing ? 'Pushing…' : 'Push to Inbound'}
              </button>
            </div>
          </div>
        </div>
      )}
    </WarehouseLayout>
  )
}
